import { TFunction } from 'i18next';
import { PaymentMethod } from '@pec/aion-ui-core/enums/Subscription';

export const schema = (
  t: TFunction,
  description: string,
  recurringBillingEnabled: boolean,
  bankTransferEnabled: boolean
) => ({
  title: t('subscriber:upgrade.title', {
    description,
    defaultValue: 'Upgrade from {{description}}'
  }),
  description: t(
    'subscriber:upgrade.description',
    'Show your commitment to creating a safer, risk-free and more compliant world for all of your employees by upgrading your membership.'
  ),
  name: 'header',
  pages: [
    {
      stepName: 'UpgradeStart',
      elements: [
        {
          type: 'mui-radiogroup',
          name: 'organizationType',
          title: t('subscriber:survey.fields.organizationType', 'Are you a contractor or a supplier?'),
          isRequired: true,
          choices: [
            {
              value: 'Contractor',
              text: t('subscriber:survey.organizationType.contractor', 'Contractor'),
              tooltip: t(
                'subscriber:survey.organizationType.contractorExplanationText',
                'Contractors are construction workers or skilled tradespeople who work predominantly onsite and are responsible for installation, maintenance or repairs.'
              )
            },
            {
              value: 'Supplier',
              text: t('subscriber:survey.organizationType.supplier', 'Supplier'),
              tooltip: t(
                'subscriber:survey.organizationType.supplierExplanationText',
                'Suppliers provide off-site goods or services for use in projects, typically construction materials, industrial supplies or other raw materials.'
              )
            }
          ],
          requiredErrorText: t('subscriber:survey.validation.makeSelection', 'Please make a selection.')
        },
        {
          type: 'panel',
          elements: [
            {
              type: 'mui-select',
              name: 'applicationType',
              titleLocation: 'hidden',
              title: t('subscriber:survey.chooseApplicationType', 'Health and Safety application type'),
              choices: [
                {
                  value: 'Contractor - Construction',
                  text: t('subscriber:survey.applicationType.construction', 'Construction'),
                  tooltip: t(
                    'subscriber:survey.applicationType.constructionDetails',
                    'Any work carried out in connection with the construction, alteration, conversion,  fitting-out, commissioning, renovation, repair, maintenance, refurbishment, demolition, decommissioning or dismantling of a structure, or preparation of a building site.'
                  )
                },
                {
                  value: 'Principal Contractor',
                  text: t('subscriber:survey.applicationType.principalContractor', 'Principal contractor'),
                  tooltip: t(
                    'subscriber:survey.applicationType.principalContractorDetails',
                    'The contractor with control over the construction phase of a project to plan, manage,  monitor and coordinate health and safety involving more than one contractor.'
                  )
                },
                {
                  value: 'Designer',
                  text: t('subscriber:survey.applicationType.designer', 'Designer'),
                  tooltip: t(
                    'subscriber:survey.applicationType.designerDetails',
                    'Individuals or organisations such as architects, consulting engineers or quantity surveyors. This includes anyone who specifies and alters designs as part of their work.'
                  )
                },
                {
                  value: 'Principal Designer',
                  text: t('subscriber:survey.applicationType.principalDesigner', 'Principal designer'),
                  tooltip: t(
                    'subscriber:survey.applicationType.principalDesignerDetails',
                    'An organisation or individual who takes the lead in planning, managing, monitoring and coordinating health and safety during the pre-construction phase of a project.'
                  )
                },
                {
                  value: 'Care',
                  text: t('subscriber:survey.applicationType.care', 'Care'),
                  visibleIf: '{info.currentSubscription} != "CHAS Elite"',
                  tooltip: t(
                    'subscriber:survey.applicationType.careDetails',
                    'An organisation that provides services such as residential nursing homes, domiciliary/day care, or other support services in the care sector.'
                  )
                },
                {
                  value: 'SSIP Approved (Non-Construction)',
                  text: t('subscriber:survey.applicationType.ssipApproved', 'SSIP approved (non-construction)'),
                  tooltip: t(
                    'subscriber:survey.applicationType.ssipApprovedDetails',
                    'Not working in connection with any construction activities. e.g. Transportation or Catering Services.'
                  )
                }
              ]
            },
            {
              type: 'mui-select',
              name: 'assessmentType',
              isRequired: true,
              titleLocation: 'hidden',
              title: t('subscriber:survey.chooseAssessmentType', 'Health and Safety assessment type'),
              choices: [
                {
                  value: 'Direct Application',
                  text: t('subscriber:survey.assessmentType.fullApplication', 'Full application'),
                  tooltip: t(
                    'subscriber:survey.assessmentType.fullApplicationDetails',
                    'A full assessment of your Health & Safety Management System, hosted online and reviewed via a qualified CHAS assessor.'
                  )
                },
                {
                  value: 'DTS',
                  text: t('subscriber:survey.assessmentType.deemToSatisfy', 'Deem to satisfy'),
                  visibleIf: '{info.currentSubscription} != "CHAS Elite"',
                  tooltip: t(
                    'subscriber:survey.assessmentType.deemToSatisfyDetails',
                    'You can ONLY obtain a Deemed to Satisfy (DTS) Certificate if you have a valid assessment with another SSIP scheme operator. Please ensure you have completed the SSIP element with your certification body, so that your certificate details are populated on the SSIP portal. If you do not hold a valid assessment with another SSIP operator, you must apply for a CHAS Assessment.'
                  )
                },
                {
                  value: 'Assured',
                  text: t('subscriber:survey.assessmentType.assuredOnSiteAudit', 'Assured (on-site audit)'),
                  visibleIf: '{applicationType} != "Care"',
                  tooltip: t(
                    'subscriber:survey.assessmentType.assuredOnSiteAuditDetails',
                    'A full assessment of your Health  & Safety Management System, in the form of an in-person visit from a qualified CHAS assessor.'
                  )
                }
              ]
            }
          ],
          visibleIf: '{organizationType} = Contractor'
        },
        {
          type: 'panel',
          description: t(
            'subscriber:survey.employeeDetailsDescription',
            'The number of employees should include all directly employed office staff including permanent, temporary, part-time and labour-only subcontractors. Bona fide subcontractors should be counted separately.'
          ),
          elements: [
            {
              type: 'mui-text',
              name: 'totalEmployeeCount',
              titleLocation: 'hidden',
              title: t('subscriber:survey.numberOfEmployees', 'Number of employees'),
              isRequired: true,
              tooltip: t(
                'subscriber:survey.tooltip.numberOfEmployees',
                'The number of employees should include all directly employed office stuff including premanent, temporary, part-time and labour-only subcontractors. Bona fide subcontractors should be counted separately.'
              ),
              requiredErrorText: t('subscriber:survey.validation.enterField', {
                defaultValue: 'Please enter {{field}}',
                field: t('subscriber:survey.validation.numberOfEmployees', 'Number of employees')
              }),
              validators: [
                {
                  type: 'numeric',
                  minValue: 1,
                  maxValue: 999999
                }
              ],
              inputType: 'number',
              limitInput: '^[0-9]*$'
            },
            {
              type: 'mui-text',
              name: 'subcontractorEmployeeCount',
              title: t('subscriber:survey.fields.bonaFideSubcontractors', 'Number of bona fide subcontractors'),
              titleLocation: 'hidden',
              isRequired: true,
              tooltip: t(
                'subscriber:survey.tooltip.bonaFideSubcontractors',
                'Bona Fide are subcontractors who work with their own tools, insurance etc. And are usually paid by invoice. We recommend that you consider the total number of effective employees you may have/use at ony one time during a 12-month rolling period.'
              ),
              requiredErrorText: t('subscriber:survey.validation.enterField', {
                defaultValue: 'Please enter {{field}}',
                field: t('subscriber:survey.validation.bonaFideSubcontractors', 'Number of bona fide subcontractors')
              }),
              validators: [
                {
                  type: 'numeric',
                  minValue: 0,
                  maxValue: 999999
                }
              ],
              inputType: 'number',
              limitInput: '^[0-9]*$'
            }
          ]
        }
      ]
    },
    {
      stepName: 'Order',
      showCompleteButton: recurringBillingEnabled ? false : true,
      elements: [
        {
          type: 'mui-selectable-subscriptions',
          name: 'subscriptionSelect',
          titleLocation: 'hidden',
          isRequired: true,
          choicesByUrl: {
            url:
              '/api/v1/products?Location={info.location}&CustomerType={organizationType}&NumOfEmployees={totalEmployeeCount}&Country={info.countryIso2Code}&OperationType=SignUp{subscriptionOptionalParams}',
            valueName: 'id',
            titleName: 'subscriptionTier',
            attachOriginalItems: true,
            path: 'products'
          },
          orderByOriginalItem: 'price:asc',
          requiredErrorText: t('subscriber:survey.validation.makeSelection', 'Please make a selection'),
          productDetails: [
            {
              description: [
                { text: t('subscriber:survey.productDetails.meetSSIP', 'Meet SSIP requirements'), icon: 'Check' },
                { text: t('subscriber:survey.productDetails.meetPAS', 'Meet PAS 91 requirements'), icon: 'Close' },
                {
                  text: t('subscriber:survey.productDetails.meetPublicSector', 'Meet public sector requirements'),
                  icon: 'Close'
                },
                {
                  text: t(
                    'subscriber:survey.productDetails.meetCommonAssessment',
                    'Meet Common Assessment requirements'
                  ),
                  icon: 'Close'
                },
                {
                  text: t(
                    'subscriber:survey.productDetails.recognisedMostComprehensive',
                    'Recognised industry-wide as the most comprehensive'
                  ),
                  icon: 'Close'
                }
              ],
              visibleIf: "{value} = 'CHAS Standard'"
            },
            {
              description: [
                { text: t('subscriber:survey.productDetails.meetSSIP', 'Meet SSIP requirements'), icon: 'Check' },
                { text: t('subscriber:survey.productDetails.meetPAS', 'Meet PAS 91 requirements'), icon: 'Check' },
                {
                  text: t('subscriber:survey.productDetails.meetPublicSector', 'Meet public sector requirements'),
                  icon: 'Close'
                },
                {
                  text: t(
                    'subscriber:survey.productDetails.meetCommonAssessment',
                    'Meet Common Assessment requirements'
                  ),
                  icon: 'Close'
                },
                {
                  text: t(
                    'subscriber:survey.productDetails.recognisedMostComprehensive',
                    'Recognised industry-wide as the most comprehensive'
                  ),
                  icon: 'Close'
                }
              ],
              visibleIf: "{value} = 'CHAS Advanced'"
            },
            {
              description: [
                { text: t('subscriber:survey.productDetails.meetSSIP', 'Meet SSIP requirements'), icon: 'Check' },
                { text: t('subscriber:survey.productDetails.meetPAS', 'Meet PAS 91 requirements'), icon: 'Check' },
                {
                  text: t('subscriber:survey.productDetails.meetPublicSector', 'Meet public sector requirements'),
                  icon: 'Check'
                },
                {
                  text: t(
                    'subscriber:survey.productDetails.meetCommonAssessment',
                    'Meet Common Assessment requirements'
                  ),
                  icon: 'Check'
                },
                {
                  text: t(
                    'subscriber:survey.productDetails.recognisedMostComprehensive',
                    'Recognised industry-wide as the most comprehensive'
                  ),
                  icon: 'Check'
                }
              ],
              isMostPopular: true,
              visibleIf: "{info.location} = GB && {value} = 'CHAS Elite'"
            },
            {
              description: [
                {
                  text: t(
                    'subscriber:survey.productDetails.AUCHASEliteText',
                    'CHAS Elite is the elite standard of CHAS accreditation and includes all assessments under Verified Contractor and seven additional risk areas: health and safety (H&S), quality, environmental, equality, diversity and inclusion (EDI), anti-bribery and corruption, corporate social responsibility (CSR) and information security.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{info.location} = AU && {value} = 'CHAS Elite'"
            },
            {
              description: [
                {
                  text: t(
                    'subscriber:survey.productDetails.GBCHASVerifiedSupplierText',
                    'Membership package tailored for Suppliers to validate their capabilities and showcase their certifications and qualifications.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{info.location} = GB && {value} = 'CHAS Verified Supplier'"
            },
            {
              description: [
                {
                  text: t(
                    'subscriber:survey.productDetails.CHASVerifiedContractorText',
                    'Verified Contractor is our entry-level CHAS assessment package, offering contractor accreditation in three areas of risk management: identity, insurance and modern slavery. Gain accreditation, begin prequalifying for contracts and win more work.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{value} = 'CHAS Verified Contractor'"
            },
            {
              description: [
                {
                  text: t(
                    'subscriber:survey.productDetails.AUCHASVerifiedSupplierText',
                    'Verified Supplier is our risk management verification service specifically tailored for material suppliers. It is a streamlined and comprehensive risk management solution that allows suppliers to highlight their compliance with supply-chain best practices, regulatory controls and international standards.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{info.location} = AU && {value} = 'CHAS Verified Supplier'"
            }
          ],
          productIcons: [
            { iconSrc: '/static/images/Standard.svg', visibleIf: "{subscriptionTier} = 'CHAS Standard'" },
            { iconSrc: '/static/images/Advanced.svg', visibleIf: "{subscriptionTier} = 'CHAS Advanced'" },
            { iconSrc: '/static/images/Elite.svg', visibleIf: "{subscriptionTier} = 'CHAS Elite'" },
            {
              iconSrc: '/static/images/VerifiedSupplier.svg',
              visibleIf: "{subscriptionTier} = 'CHAS Verified Supplier'"
            },
            {
              iconSrc: '/static/images/VerifiedContractor.svg',
              visibleIf: "{subscriptionTier} = 'CHAS Verified Contractor'"
            }
          ],
          strings: {
            year: t('subscriber:survey.subscription.year', '/year*'),
            yearNoTax: t('subscriber:survey.subscription.yearNoTax', '/year'),
            select: t('subscriber:survey.subscription.select', 'Select'),
            selected: t('subscriber:survey.subscription.selected', 'Selected'),
            mostPopular: t('subscriber:survey.subscription.mostPopular', 'Most popular!')
          },
          filterChoicesOnRender:
            "expandChoicesByUrl({item}, productType) != 'Services' && expandChoicesByUrl({item}, subscriptionTier) != 'CHAS Joining Fee'",
          price: 'formatPrice(getPrice({value}, getPriceWithDiscount({value}, true), false))'
        },
        {
          type: 'mui-additional-products',
          name: 'additionalProducts',
          title: t('subscriber:survey.additionalProducts.titleSupport', 'Choose your support package'),
          description: t(
            'subscriber:survey.additionalProductsDescription',
            'Whether you require a fast tracked application or additional support and guidance, we offer a variety of different options to suit your budget and needs.'
          ),
          isRequired: true,
          requiredErrorText: t('subscriber:survey.validation.makeSelection', 'Please make a selection'),
          choicesFromQuestion: 'subscriptionSelect',
          filterChoicesOnRender: "expandChoicesByUrl({item}, productType) = 'Services'",
          selfServeText: t('subscriber:survey.additionalProducts.selfServe', 'Self Serve'),
          visibleIf: '{subscriptionSelect} notempty && !!expandChoicesByUrl(Services, id, productType)',
          price: 'formatPrice(getPrice({value}, getPriceWithDiscount({value}, true), false))',
          productDetails: [
            {
              description: [
                {
                  text: t(
                    'subscriber:survey.additionalProducts.assessmentTurnaround',
                    '{value}-day assessment turnaround**'
                  ).replace('{value}', '2'),
                  icon: ''
                },
                {
                  text: t(
                    'subscriber:survey.additionalProducts.awardWinningCustomerSupport',
                    'Award-winning customer support'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{value} = 'Fast Track'"
            },
            {
              description: [
                {
                  text: t(
                    'subscriber:survey.additionalProducts.assessmentTurnaround',
                    '{value}-day assessment turnaround**'
                  ).replace('{value}', '2'),
                  icon: ''
                },
                {
                  text: t(
                    'subscriber:survey.additionalProducts.awardWinningCustomerSupport',
                    'Award-winning customer support'
                  ),
                  icon: ''
                },
                {
                  text: t(
                    'subscriber:survey.additionalProducts.dedicatedOneTwoOneAssistance',
                    'Dedicated 1-2-1 health and safety expert assistance'
                  ),
                  icon: ''
                },
                {
                  text: t(
                    'subscriber:survey.additionalProducts.documentsTemplatesSupport',
                    'Additional support documents and templates'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{value} = 'CHAS Assist'"
            }
          ],
          strings: {
            selfServeAdditionalProducts: [
              t('subscriber:survey.additionalProducts.assessmentTurnaround', '{value}-day assessment turnaround**')
                .replace('{value}', '10')
                .replace('**', ''),
              t('subscriber:survey.additionalProducts.awardWinningCustomerSupport', 'Award-winning customer support')
            ],
            includesFastTrack: t('subscriber:survey.additionalProducts.includesFastTrack', 'Includes Fast Track')
          }
        },
        {
          type: 'html',
          html: '<hr class="order-divider"/>'
        },
        {
          type: 'mui-checkbox',
          name: 'dataSharingConsent',
          title: t(
            'subscriber:dataSharingConsent.labelText',
            'I agree to relevant information held by CHAS being shared with other Recognised Assessment Bodies for the Common Assessment Standard. I am an authorised representative with sufficient delegated powers to provide the consent specified above. {0}'
          ),
          links: [
            {
              text: t(
                'subscriber:dataSharingConsent.link',
                'Learn more about the Common Assessment Standard and data sharing.'
              ),
              href:
                'https://veriforce-prod-cp-cacentral1-versioned.s3.ca-central-1.amazonaws.com/chas/Key-Questions-for-Data-Sharing.pdf'
            }
          ],
          titleLocation: 'hidden',
          visibleIf:
            "{info.location} = GB && expandChoicesByUrl({subscriptionSelect}, subscriptionTier) = 'CHAS Elite'",
          clearIfInvisible: 'onHidden'
        },
        {
          type: 'mui-discount',
          name: 'discountCode',
          title: t('subscriber:survey.fields.discount.referenceCode', 'Reference Code'),
          description: t(
            'subscriber:survey.fields.discount.description',
            'Optional: if you have been supplied a reference code by a client or trade association, please enter it here.'
          ),
          validatorUrl: '/api/v1/products/validateDiscountCode',
          enableIf: '{subscriptionSelect} notempty',
          strings: {
            apply: t('subscriber:common.apply', 'Apply'),
            remove: t('subscriber:common.remove', 'Remove'),
            codeApplied: t('subscriber:survey.fields.discount.codeApplied', 'applied'),
            codeNotFound: t('subscriber:survey.fields.discount.codeNotFound', "Code doesn't exist"),
            codeExpired: t('subscriber:survey.fields.discount.codeExpired', 'Code is expired'),
            codeCannotByApplied: t(
              'subscriber:survey.fields.discount.codeCannotByApplied',
              "Code doesn't apply to selected items"
            ),
            clientNotFound: t('subscriber:survey.fields.discount.clientNotFound', 'Client not found'),
            codeIsNotValid: t('subscriber:survey.fields.discount.codeIsNotValid', 'Code is not valid'),
            unknownError: t('subscriber:survey.fields.discount.unknownError', 'Unknown error, please try again later.')
          }
        },
        ...(bankTransferEnabled
          ? [
              {
                type: 'mui-radiogroup',
                name: 'paymentMethod',
                title: t('subscriber:fields.paymentMethod', 'Payment Method'),
                isRequired: true,
                choices: [
                  {
                    value: PaymentMethod.CARD,
                    text: t('subscriber:paymentMethod.card', 'Credit / Debit Card'),
                    iconSrc: '/static/images/PaymentMethods.png'
                  },
                  {
                    value: PaymentMethod.BANK_TRANSFER,
                    text: t('subscriber:paymentMethod.bankTransfer', 'Bank Transfer'),
                    description: t(
                      'subscriber:paymentMethod.bankTransferDescription',
                      'You will receive a Stripe invoice for payment. Access will be restricted until payment is made in full.'
                    )
                  }
                ],
                requiredErrorText: t('subscriber:validation.makeSelection', 'Please make a selection.')
              }
            ]
          : []),
        {
          type: 'html',
          name: 'Cost Breakdown',
          html: `
                      <span class="pricing-explanation sd-description sd-title cost-breakdown">
                      {subscriptionSelect} - {basePackagePrice}* <br />
                        <span class="pricing-explanation sd-description sd-title cost-breakdown">
                      Joining fee - {joiningFee}*

                `,
          visibleIf: '!!expandChoicesByUrl({subscriptionSelect}, subscriptionTier)'
        },
        {
          type: 'html',
          name: 'Cost Breakdown',
          html: `
                      <span class="pricing-explanation sd-description sd-title cost-breakdown">
                      {additionalProductName} - {additionalProductPrice}* <br />

                `,
          visibleIf: '!!{additionalProductName}'
        },
        {
          type: 'html',
          name: 'total',
          html: ` <div class='total-price'>
                      <span class='sd-title'>${t(
                        'subscriber:survey.selectedConnectionsList.total',
                        'Total:'
                      )} {subscriptionTotalPrice} </span> <span class='total-price-explanation-text'> {subscriptionTotalPriceExplanationText} </span>
                  </div>
                `,
          visibleIf: '!!expandChoicesByUrl({subscriptionSelect}, subscriptionTier)'
        },
        ...(recurringBillingEnabled
          ? [
              {
                type: 'mui-privacy-consent',
                name: 'privacyConsent',
                titleLocation: 'hidden',
                title: t(
                  'subscriber:privacyConsent.title',
                  'By placing this order I confirm that I have read and agree with the {0} and {1}.'
                ),
                links: [
                  {
                    text: t('subscriber:privacyConsent.tos', 'Terms of Service'),
                    href: 'https://veriforce.com/terms-of-service'
                  },
                  {
                    text: t('subscriber:privacyConsent.privacyPolicy', 'Privacy Policy'),
                    href: 'https://veriforce.com/privacy-policy'
                  }
                ]
              }
            ]
          : []),
        {
          type: 'expression',
          name: 'totalExplanation',
          titleLocation: 'hidden',
          expression: `
            iif(
              {taxRate.isActive} && getJoiningFee() > 0,
              '${t(
                'subscriber:survey.pricesExcludeTaxAndJoiningFee',
                '*Prices exclude {tax}. New members pay a one-off joining fee of {price}.'
              )
                .replace('{tax}', "' + {taxRate.name} + '")
                .replace('{price}', "' + formatPrice(getPriceWithDiscount(getJoiningFee('id'), true)) + '")}',
              iif(
                {taxRate.isActive},
                '${t('subscriber:survey.pricesExcludeTax', '* Prices exclude {tax}').replace(
                  '{tax}',
                  "' + {taxRate.name} + '"
                )}',
                iif({country} = AU && {taxRate.isActive} = false, '${t(
                  'subscriber:survey.notRegisteredForTax',
                  'Not registered for {tax}.'
                ).replace('{tax}', "' + {taxRate.name} + '")}', '')
              ) +
              iif(getJoiningFee() > 0, '${t(
                'subscriber:survey.pricesExcludeJoiningFee',
                '* New members pay a one-off joining fee of {price}'
              ).replace('{price}', "' + formatPrice(getPriceWithDiscount(getJoiningFee('id'), true)) + '")}', '')
            )
          `
        },
        {
          type: 'html',
          name: 'additionalProductsExplanation',
          titleLocation: 'hidden',
          visibleIf: '{subscriptionSelect} notempty && !!expandChoicesByUrl(Services, id, productType)',
          html: `<p class='sd-question__html'>
           ${t(
             'subscriber:survey.additionalProductsExplanation_1',
             '**Initial H&S assessment completed within 2 working days of submission.'
           )} <br>
           ${t(
             'subscriber:survey.additionalProductsExplanation_2',
             'DTS checks will be completed within 1 working day.'
           )} <br>
           ${t(
             'subscriber:survey.additionalProductsExplanation_3',
             'Other Elite Assessed modules may take up to 5 working days.'
           )}<br/>
           ${t(
             'subscriber:survey.additionalProductsExplanation_4',
             'Fast Track is not applicable for on-site audits.'
           )}<br/>
          <p/>`
        },
        {
          type: 'html',
          html: `<p class='pricing-explanation'>${t(
            'subscriber:survey.priceExplanationGeneral',
            'CHAS uses a dynamic pricing system and prices vary depending on certification level and the number of employees your business has.'
          )} </p>`
        },
        ...(recurringBillingEnabled
          ? [
              {
                type: 'html',
                html: `<p class='pricing-explanation'>${t(
                  'subscriber:autoRenewalInfo',
                  'The membership is a continuous service billed annually. You have the right to opt out of auto-renewal by providing us with notice at least 14 days prior to the renewal.'
                )}</p>`
              }
            ]
          : [])
      ],
      completePage: true
    }
  ],
  calculatedValues: [
    {
      name: 'subscriptionOptionalParams',
      expression:
        "iif({isSubscriptionDisplayed}, '&ready=true' + iif({applicationType} notempty, '&ApplicationType=' + {applicationType}, '') + iif({assessmentType} notempty, '&AssessmentType=' + {assessmentType}, ''), '')"
    },
    {
      name: 'totalWithDiscountAmount',
      expression:
        "sum(getPriceWithDiscount({subscriptionSelect}), getPriceWithDiscount({additionalProducts}), iif({subscriptionSelect}, getPriceWithDiscount(getJoiningFee('id')), 0))"
    },
    {
      name: 'totalAmount',
      expression:
        'sum(expandChoicesByUrl({subscriptionSelect}, price), expandChoicesByUrl({additionalProducts}, price), iif({subscriptionSelect}, getJoiningFee(), 0))'
    },
    {
      name: 'relativeDiscountAmount',
      expression: 'formatPrice({totalAmount} - {totalWithDiscountAmount})'
    },
    {
      name: 'subscriptionTotalPrice',
      expression:
        'formatPrice(sum(getPriceTotal(getPriceWithDiscount({subscriptionSelect})), getPriceTotal(getPriceWithDiscount({additionalProducts})), iif({subscriptionSelect}, getPriceTotal(getPriceWithDiscount(getJoiningFee("id"))), 0)))'
    },
    {
      name: 'subscriptionTotalPriceExplanationText',
      expression: `
        iif({taxRate.isActive}, '${t('subscriber:survey.totalTaxExplanation', '(inc {tax})').replace(
          '{tax}',
          "' + {taxRate.name} + '"
        )}', ' ')
      `
    },
    {
      name: 'basePackagePrice',
      expression: 'iif({subscriptionSelect}, formatPrice(expandChoicesByUrl({subscriptionSelect}, price)), -1)'
    },
    {
      name: 'additionalProductPrice',
      expression: 'iif({additionalProducts}, formatPrice(expandChoicesByUrl({additionalProducts}, price), 0)'
    },
    {
      name: 'additionalProductName',
      expression: 'showAdditionalProductName()'
    },
    {
      name: 'joiningFee',
      expression: 'iif({subscriptionSelect}, formatPrice(getJoiningFee()), true)'
    }
  ],
  clearInvisibleValues: 'onHiddenContainer',
  showCompletedPage: false,
  showQuestionNumbers: 'off',
  questionErrorLocation: 'bottom',
  pagePrevText: t('subscriber:survey.navigation.back', 'Back'),
  pageNextText: t('subscriber:survey.navigation.next', 'Next'),
  completeText: t('subscriber:survey.navigation.toCheckout', 'Upgrade now'),
  showNavigationButtons: false
});
